import React from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SearchButtonStyle } from '@/types/codeInjection';

interface SearchButtonStyleEditorProps {
  restState: SearchButtonStyle;
  hoverState: SearchButtonStyle;
  onRestStateChange: (style: SearchButtonStyle) => void;
  onHoverStateChange: (style: SearchButtonStyle) => void;
  primaryColor: string;
  secondaryColor: string;
}

/**
 * SearchButtonStyleEditor - Style editor for search button rest and hover states
 * Similar to MenuItemTextEditor but focused on background and SVG colors
 */
const SearchButtonStyleEditor: React.FC<SearchButtonStyleEditorProps> = ({
  restState,
  hoverState,
  onRestStateChange,
  onHoverStateChange,
  primaryColor,
  secondaryColor
}) => {
  const [activeTab, setActiveTab] = React.useState<'rest' | 'hover'>('rest');
  
  const colorPresets = [
    { name: 'Primary', color: primaryColor },
    { name: 'Secondary', color: secondaryColor },
    { name: 'White', color: '#ffffff' },
    { name: 'Black', color: '#2D2D2D' },
    { name: 'Sand', color: '#FFFBF2' },
    { name: 'Gold', color: '#c5b068' },
    { name: 'Green', color: '#3cc35c' },
    { name: 'Blue', color: '#3f92ff' }
  ];

  const currentState = activeTab === 'rest' ? restState : hoverState;
  const updateCurrentState = activeTab === 'rest' ? onRestStateChange : onHoverStateChange;

  const handleColorChange = (property: keyof SearchButtonStyle, value: string) => {
    updateCurrentState({
      ...currentState,
      [property]: value
    });
  };

  const handlePresetClick = (property: keyof SearchButtonStyle, color: string) => {
    handleColorChange(property, color);
  };

  const resetToDefaults = () => {
    if (activeTab === 'rest') {
      onRestStateChange({
        backgroundColor: primaryColor,
        svgColor: '#ffffff'
      });
    } else {
      onHoverStateChange({
        backgroundColor: secondaryColor,
        svgColor: '#ffffff'
      });
    }
  };

  const copyRestToHover = () => {
    onHoverStateChange({ ...restState });
    setActiveTab('hover');
  };

  return (
    <div className="space-y-6">
      {/* Copy Rest → Hover Button */}
      <div className="flex justify-center">
        <Button
          variant="outline"
          size="sm"
          onClick={copyRestToHover}
          className="text-xs"
        >
          Copy Rest → Hover
        </Button>
      </div>

      {/* Rest/Hover State Tabs */}
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'rest' | 'hover')}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="rest">Rest State</TabsTrigger>
          <TabsTrigger value="hover">Hover State</TabsTrigger>
        </TabsList>

        <TabsContent value="rest" className="space-y-4 mt-4">
          <SearchButtonStateEditor
            state={restState}
            onStateChange={onRestStateChange}
            colorPresets={colorPresets}
            onPresetClick={handlePresetClick}
            onResetToDefaults={resetToDefaults}
          />
        </TabsContent>

        <TabsContent value="hover" className="space-y-4 mt-4">
          <SearchButtonStateEditor
            state={hoverState}
            onStateChange={onHoverStateChange}
            colorPresets={colorPresets}
            onPresetClick={handlePresetClick}
            onResetToDefaults={resetToDefaults}
          />
        </TabsContent>
      </Tabs>

      {/* Preview */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Preview</Label>
        <div className="p-4 bg-gray-50 rounded-md flex items-center justify-center">
          <div 
            className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer"
            style={{ 
              backgroundColor: activeTab === 'rest' ? restState.backgroundColor : hoverState.backgroundColor 
            }}
          >
            <svg 
              width="20" 
              height="20" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke={activeTab === 'rest' ? restState.svgColor : hoverState.svgColor}
              strokeWidth="2"
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8"/>
              <path d="21 21l-4.35-4.35"/>
            </svg>
          </div>
          <span className="ml-3 text-sm text-gray-600">
            Sample Search Button ({activeTab === 'rest' ? 'Rest' : 'Hover'})
          </span>
        </div>
      </div>
    </div>
  );
};

interface SearchButtonStateEditorProps {
  state: SearchButtonStyle;
  onStateChange: (style: SearchButtonStyle) => void;
  colorPresets: { name: string; color: string }[];
  onPresetClick: (property: keyof SearchButtonStyle, color: string) => void;
  onResetToDefaults: () => void;
}

const SearchButtonStateEditor: React.FC<SearchButtonStateEditorProps> = ({
  state,
  onStateChange,
  colorPresets,
  onPresetClick,
  onResetToDefaults
}) => {
  const handleColorChange = (property: keyof SearchButtonStyle, value: string) => {
    onStateChange({
      ...state,
      [property]: value
    });
  };

  return (
    <div className="space-y-4">
      {/* Background Color */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Background Color</Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="w-full justify-start gap-2"
            >
              <div 
                className="w-4 h-4 rounded border border-gray-300"
                style={{ backgroundColor: state.backgroundColor }}
              />
              {state.backgroundColor}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64">
            <div className="space-y-3">
              <Label className="text-sm font-medium">Pick background color</Label>
              <Input
                type="color"
                value={state.backgroundColor}
                onChange={(e) => handleColorChange('backgroundColor', e.target.value)}
                className="w-full h-10"
              />
              <Input
                type="text"
                value={state.backgroundColor}
                onChange={(e) => handleColorChange('backgroundColor', e.target.value)}
                placeholder="#ffffff"
                className="font-mono text-sm"
              />
            </div>
          </PopoverContent>
        </Popover>
        
        {/* Background Color Presets */}
        <div className="grid grid-cols-4 gap-1">
          {colorPresets.map((preset) => (
            <Button
              key={`bg-${preset.name}`}
              variant="outline"
              size="sm"
              onClick={() => onPresetClick('backgroundColor', preset.color)}
              className="h-8 p-1 flex items-center gap-1"
            >
              <div 
                className="w-3 h-3 rounded border border-gray-300 flex-shrink-0"
                style={{ backgroundColor: preset.color }}
              />
              <span className="text-xs">{preset.name}</span>
            </Button>
          ))}
        </div>
      </div>

      {/* SVG Color */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">SVG Color</Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="w-full justify-start gap-2"
            >
              <div 
                className="w-4 h-4 rounded border border-gray-300"
                style={{ backgroundColor: state.svgColor }}
              />
              {state.svgColor}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64">
            <div className="space-y-3">
              <Label className="text-sm font-medium">Pick SVG color</Label>
              <Input
                type="color"
                value={state.svgColor}
                onChange={(e) => handleColorChange('svgColor', e.target.value)}
                className="w-full h-10"
              />
              <Input
                type="text"
                value={state.svgColor}
                onChange={(e) => handleColorChange('svgColor', e.target.value)}
                placeholder="#ffffff"
                className="font-mono text-sm"
              />
            </div>
          </PopoverContent>
        </Popover>
        
        {/* SVG Color Presets */}
        <div className="grid grid-cols-4 gap-1">
          {colorPresets.map((preset) => (
            <Button
              key={`svg-${preset.name}`}
              variant="outline"
              size="sm"
              onClick={() => onPresetClick('svgColor', preset.color)}
              className="h-8 p-1 flex items-center gap-1"
            >
              <div 
                className="w-3 h-3 rounded border border-gray-300 flex-shrink-0"
                style={{ backgroundColor: preset.color }}
              />
              <span className="text-xs">{preset.name}</span>
            </Button>
          ))}
        </div>
      </div>

      {/* Reset Button */}
      <Button
        variant="outline"
        size="sm"
        onClick={onResetToDefaults}
        className="w-full"
      >
        Reset to Defaults
      </Button>
    </div>
  );
};

export default SearchButtonStyleEditor;